import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Konn3ctHeader from "../Header & Footer/Konn3ctHeader";
import Footer from "../Header & Footer/kfooter";
import { fetchEventByRef } from "./EventsApi";
import { useCountry } from "../Context/CountryContext";
import { formatCurrency } from "../Utils/formatCurrency";
import {
  initializePaidEventCheckout,
  openPaystackPopup,
  preloadPaystackInline,
  registerFreeEvent,
  verifyPaidEventCheckout,
} from "../Services/eventCommerceApi";
import { trackEvent } from "../Analytics/analytics";

const EventDetails = () => {
  const { ref: routeRef } = useParams();
  const { country } = useCountry();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [submitLoading, setSubmitLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [message, setMessage] = useState("");
  const [paymentState, setPaymentState] = useState("idle");
  const [checkoutInfo, setCheckoutInfo] = useState({
    localReference: null,
    ticketNumber: null,
  });

  useEffect(() => {
    let alive = true;

    const loadEvent = async () => {
      if (!routeRef) {
        setLoadError("Event reference is missing.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError("");

      try {
        const data = await fetchEventByRef(routeRef, country.code);

        if (!alive) return;

        if (!data) {
          setEvent(null);
          setLoadError("Event not found.");
          return;
        }

        setEvent(data);

        trackEvent("event_detail_view", {
          event_ref: data.ref || routeRef,
          event_id: data.eventId || undefined,
          country: country.code,
          free: data.free,
          currency: data.currency || country.currency,
        });
      } catch (error) {
        console.error("Unable to load event:", error);

        if (alive) {
          setEvent(null);
          setLoadError(error?.message || "Unable to load this event. Please try again.");
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    };

    loadEvent();

    return () => {
      alive = false;
    };
  }, [routeRef, country.code, country.currency]);

  useEffect(() => {
    if (event?.free === 0) {
      preloadPaystackInline().catch((error) => {
        console.warn("Paystack preload failed:", error);
      });
    }
  }, [event?.free]);

  const isPaid = event?.free === 0;

  const displayCurrency = useMemo(() => event?.currency || country.currency, [country.currency, event?.currency]);

  const displayPrice = useMemo(() => {
    if (!event) return "";
    if (!isPaid) return "FREE";

    return formatCurrency({
      amount: event.amount,
      currency: displayCurrency,
      locale: country.locale,
    });
  }, [country.locale, displayCurrency, event, isPaid]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleFreeRegistration = async ({ name, email, phone }) => {
    try {
      await registerFreeEvent({
        ref: event?.ref || routeRef,
        name,
        email,
        phone,
      });

      setRegistered(true);
      setMessage("Registration successful.");

      trackEvent("free_event_registration_completed", {
        event_ref: event?.ref || routeRef,
        event_id: event?.eventId || undefined,
        country: country.code,
      });
    } catch (error) {
      if (error?.code === "ALREADY_REGISTERED") {
        setRegistered(true);
        setMessage("You are already registered for this event.");
        return;
      }

      throw error;
    }
  };

  const handlePaidRegistration = async ({ name, email, phone }) => {
    if (!event?.eventId) {
      throw new Error("This paid event is missing its checkout ID. Please reopen the event from the Events page.");
    }

    setPaymentState("initializing");

    let checkout;

    try {
      checkout = await initializePaidEventCheckout({
        eventId: event.eventId,
        name,
        email,
        phone,
      });
    } catch (error) {
      if (error?.code === "ALREADY_PAID") {
        setPaymentState("success");
        setRegistered(true);
        setMessage("This ticket has already been paid for.");
        return;
      }

      throw error;
    }

    setCheckoutInfo({
      localReference: checkout.localReference,
      ticketNumber: checkout.ticketNumber,
    });

    trackEvent("paid_event_checkout_initialized", {
      event_ref: event?.ref || routeRef,
      event_id: event.eventId,
      country: country.code,
      provider: "paystack",
      local_reference: checkout.localReference || undefined,
      ticket_number: checkout.ticketNumber || undefined,
    });

    setPaymentState("opening");
    setMessage("Opening secure payment...");

    await openPaystackPopup({
      accessCode: checkout.accessCode,
      onLoad: () => {
        setPaymentState("open");
        setMessage("");
      },
      onSuccess: async (transaction) => {
        const paystackRef = transaction?.reference || transaction?.trxref || checkout.localReference;

        setPaymentState("verifying");
        setMessage("Verifying payment and generating your ticket...");

        try {
          const verifyResult = await verifyPaidEventCheckout({
            reference: paystackRef || checkout.localReference,
          });

          if (verifyResult?.ticket_number) {
            setCheckoutInfo((prev) => ({
              ...prev,
              ticketNumber: verifyResult.ticket_number,
            }));
          }

          setPaymentState("success");
          setRegistered(true);
          setMessage("Payment completed and verified! Your event ticket and receipt have been sent to your email.");
        } catch (verifyError) {
          console.warn("Client-side verification warning:", verifyError);
          // Fallback: If network glitch occurred during client verify, payment succeeded on Paystack
          setPaymentState("success");
          setRegistered(true);
          setMessage("Payment received! Your ticket and confirmation are being processed and sent to your email.");
        }

        trackEvent("paid_event_payment_completed", {
          event_ref: event?.ref || routeRef,
          event_id: event.eventId,
          country: country.code,
          provider: "paystack",
          paystack_reference: transaction?.reference || transaction?.trxref || undefined,
          local_reference: checkout.localReference || undefined,
          ticket_number: checkout.ticketNumber || undefined,
        });
      },
      onCancel: () => {
        setPaymentState("cancelled");
        setMessage("Payment was not completed. Click Register when you are ready to try again.");

        trackEvent("paid_event_payment_cancelled", {
          event_ref: event?.ref || routeRef,
          event_id: event.eventId,
          country: country.code,
          provider: "paystack",
        });
      },
      onError: (error) => {
        console.error("Paystack popup error:", error);
        setPaymentState("error");
        setMessage(error?.message || "Unable to load Paystack payment. Please try again.");
      },
    });
  };

  const handleRegistration = async (e) => {
    e.preventDefault();

    if (submitLoading || paymentState === "open") {
      return;
    }

    setMessage("");

    const submissionRef = event?.ref || routeRef;
    const name = formData.name.trim();
    const email = formData.email.trim();
    const phone = formData.phone.trim();

    if (!submissionRef) {
      setMessage("Event reference is missing. Please reopen the event from the events page.");
      return;
    }

    if (!name || !email || !phone) {
      setMessage("Please complete your name, email and phone number.");
      return;
    }

    setSubmitLoading(true);

    try {
      if (isPaid) {
        await handlePaidRegistration({ name, email, phone });
      } else {
        await handleFreeRegistration({ name, email, phone });
      }
    } catch (error) {
      console.error("Event registration failed:", error);
      setPaymentState(isPaid ? "error" : "idle");
      setMessage(error?.message || "Unable to register for this event. Please try again.");

      trackEvent("event_registration_failed", {
        event_ref: event?.ref || routeRef,
        event_id: event?.eventId || undefined,
        country: country.code,
        paid: isPaid,
        message: error?.message || "Unknown error",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 rounded-full border-4 border-blue-100 border-t-[#1A4699] animate-spin" />
          <p className="mt-4 text-sm text-gray-500">Loading event...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex flex-col text-gray-800">
        <Konn3ctHeader />
        <main className="flex-1 flex items-center justify-center px-6 pt-24">
          <div className="text-center">
            <h1 className="text-2xl font-bold">Event unavailable</h1>
            <p className="mt-2 text-gray-500">{loadError || "Event not found."}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  const paymentBusy = ["initializing", "opening", "open"].includes(paymentState);

  return (
    <div className="min-h-screen flex flex-col text-gray-800">
      <Helmet>
        <title>{event.title} | Konn3ct</title>

        <meta name="description" content={event.description?.slice(0, 160)} />

        <meta property="og:title" content={event.title} />
        <meta property="og:description" content={event.description?.slice(0, 160)} />
        <meta property="og:image" content={event.logo} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={event.title} />
        <meta name="twitter:description" content={event.description?.slice(0, 160)} />
        <meta name="twitter:image" content={event.logo} />
      </Helmet>

      <Konn3ctHeader />

      <main className="flex-1 max-w-6xl w-full mx-auto px-6 pb-6 pt-28 lg:px-12 lg:pb-12 lg:pt-32 font-sans">
        <div className="rounded-xl overflow-hidden shadow-sm bg-gray-100">
          <img src={event.logo} alt={event.title} className="w-full h-[240px] sm:h-[320px] lg:h-[380px] object-cover" />
        </div>

        <p className="text-sm text-gray-500 mt-6">{event.dateString}</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-8">
          <section className="md:col-span-2">
            <div className="flex flex-wrap items-center gap-3">
              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  isPaid ? "bg-blue-50 text-blue-700" : "bg-green-50 text-green-700"
                }`}
              >
                {isPaid ? "PAID EVENT" : "FREE EVENT"}
              </span>

              <span className="text-sm font-semibold text-gray-900">{displayPrice}</span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-4">{event.title}</h1>

            <p className="text-sm text-gray-600 mt-2">
              By <span className="font-medium">{event.organizer}</span>
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              <div className="rounded-xl border border-gray-200 p-4">
                <h2 className="text-gray-800 font-semibold">Date and time</h2>
                <p className="text-gray-600 text-sm mt-1">{event.fullDate}</p>
              </div>

              <div className="rounded-xl border border-gray-200 p-4">
                <h2 className="text-gray-800 font-semibold">Location</h2>
                <p className="text-gray-600 text-sm mt-1">{event.location}</p>
              </div>
            </div>

            <div className="mt-8">
              <h2 className="text-gray-900 text-xl font-semibold mb-3">About this event</h2>

              <p className="text-sm text-gray-600 leading-7 whitespace-pre-line">{event.description}</p>
            </div>
          </section>

          <aside className="border border-gray-200 rounded-xl p-6 shadow-sm h-fit md:sticky md:top-28">
            {registered ? (
              <div className="text-center py-8">
                <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-5">
                  <svg
                    className="w-12 h-12 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>

                <h2 className="text-xl font-bold text-green-600">Successfully Registered</h2>

                <p className="text-gray-600 text-sm mt-3">
                  {isPaid
                    ? "Your payment was completed. Konn3ct will finalize the ticket from the payment confirmation and send the event details to you."
                    : "Your event registration has been completed."}
                </p>

                {isPaid && checkoutInfo.ticketNumber && (
                  <div className="mt-5 rounded-lg bg-gray-50 p-3 text-left">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Ticket number</p>
                    <p className="mt-1 break-all text-sm font-semibold text-gray-900">{checkoutInfo.ticketNumber}</p>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="mb-5">
                  <h2 className="text-gray-900 font-semibold text-lg">Register for this event</h2>
                  <p className="text-sm text-gray-500 mt-1">Enter your details below.</p>
                </div>

                {message && (
                  <div
                    className={`mb-4 rounded-lg border p-3 ${
                      paymentState === "error"
                        ? "border-red-200 bg-red-50"
                        : paymentState === "cancelled"
                          ? "border-amber-200 bg-amber-50"
                          : "border-blue-200 bg-blue-50"
                    }`}
                  >
                    <p
                      className={`text-sm leading-5 ${
                        paymentState === "error"
                          ? "text-red-700"
                          : paymentState === "cancelled"
                            ? "text-amber-800"
                            : "text-blue-800"
                      }`}
                    >
                      {message}
                    </p>
                  </div>
                )}

                <form className="space-y-3" onSubmit={handleRegistration}>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    autoComplete="name"
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm"
                  />

                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm"
                  />

                  <input
                    type="tel"
                    name="phone"
                    placeholder="Enter your phone number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    autoComplete="tel"
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm"
                  />

                  <button
                    type="submit"
                    disabled={submitLoading || paymentBusy}
                    className={`w-full rounded-lg py-3 text-sm font-semibold text-white transition ${
                      submitLoading || paymentBusy ? "bg-blue-400 cursor-not-allowed" : "bg-[#1A4699] hover:bg-blue-800"
                    }`}
                  >
                    {submitLoading || paymentBusy ? "Please wait..." : "Register"}
                  </button>
                </form>
              </>
            )}
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default EventDetails;
